import React, { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import { SelectField, TextField, SegmentedField } from '../shared/FormField';
import { INSTITUTION_TYPES } from '../data/enums';
import { useLocationOptions } from '../shared/useLocationOptions';

const parseError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Unable to update institution.';

export default function EditInstitutionModal({ isOpen, onClose, onSave, row }) {
  const [form, setForm] = useState({ name: '', institution_type: '', state: '' });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { stateOptions } = useLocationOptions();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (row) {
      setForm({
        name: row.name ?? '',
        institution_type: row.institution_type ?? '',
        state: row.state ?? '',
      });
      setStatus('idle');
      setErrMsg('');
      setSubmitting(false);
    }
  }, [row]);

  const submit = async () => {
    if (submitting) return;
    if (!form.name.trim() || !form.institution_type) return;
    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave({
        uuid: row.uuid,
        name: form.name.trim(),
        institution_type: form.institution_type,
        state: form.state.trim() || null,
        is_active: row.is_active ?? true,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseError(error));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    onClose();
  };

  return (
    <Modal
      title="Edit Institution"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle="Institution Updated"
      successMessage="The institution changes were saved to the backend."
      isSubmitting={submitting}
    >
      <TextField
        label="Institution Name"
        required
        value={form.name}
        onChange={set('name')}
        placeholder="Enter official institution name"
      />
      <SegmentedField
        label="Institution Type"
        required
        value={form.institution_type}
        onChange={set('institution_type')}
        options={INSTITUTION_TYPES}
      />
      <SelectField
        label="State"
        value={form.state}
        onChange={set('state')}
        options={stateOptions}
        placeholder="Select State (optional)"
        note="Null = federal institution - no state scope."
      />
    </Modal>
  );
}
